import React, { useCallback, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Slider, Typography } from "@mui/material";
import Cropper from "react-easy-crop";
import CustomButton from "./CustomButton";
import apiClient from "../../utils/apiClient";
import { useUser } from "../../pages/UserContext";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";

// Output is always a fixed 320x320 square, regardless of the source image's
// own dimensions/aspect ratio — the user drags/zooms within the crop circle
// to choose which part of their photo becomes the profile picture.
const OUTPUT_SIZE = 320;

const getCroppedImageBlob = (imageSrc, cropPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE
      );
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Gagal memproses gambar"))),
        "image/jpeg",
        0.9
      );
    };
    image.onerror = reject;
  });

// Two modes:
// - onCropped provided: just crops and hands the blob back to the caller to
//   stage locally (e.g. bundled into a bigger form submit later) — nothing
//   is sent to the server here.
// - onCropped omitted: crops and uploads immediately (original behavior),
//   for callers with no surrounding form to bundle the photo into. Calls
//   the optional onUploaded callback afterwards so the caller can refresh
//   its own photo-derived state without a full page reload.
const PhotoCropUploadModal = ({ open, onClose, imageSrc, email, onCropped, onUploaded }) => {
  const userDetails = useUser();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels);

      if (onCropped) {
        onCropped(blob);
        onClose();
        return;
      }

      const formData = new FormData();
      formData.append("foto", blob, "profile.jpg");
      formData.append("email", email);

      await apiClient.put(
        "/users/profile-photo",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      showSuccessToast("Pembaharuan Foto Profil Berhasil");
      if (userDetails?.refetchUser) {
        await userDetails.refetchUser();
      }
      onUploaded?.();
      onClose();
    } catch (error) {
      showErrorToast("Gagal memproses Foto Profil. Coba lagi.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sesuaikan Foto Profil</DialogTitle>
      <DialogContent>
        <Box sx={{ position: "relative", width: "100%", height: 300, bgcolor: "#333", borderRadius: 1 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>

        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Perbesar
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value)}
          />
        </Box>

        <Box sx={{ mt: 1, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <CustomButton
            text="Batal"
            baseColor="#A92027"
            hoverColor="#8C1016"
            isFullWidth={false}
            onClick={onClose}
            disabled={uploading}
          />
          <CustomButton
            text={uploading ? "Memproses..." : onCropped ? "Gunakan Foto Ini" : "Upload Foto"}
            baseColor="#003158"
            hoverColor="#00213A"
            isFullWidth={false}
            onClick={handleConfirm}
            disabled={uploading}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropUploadModal;
